import React, { ReactElement, useContext } from 'react';
import styled from 'styled-components';
import { BaseDiv, BaseImg } from '../../common/BaseElements';
import { IIconBaseProps } from '../../common/interfaces';
import { Badge } from '../../components/Badge/Badge';
import { IShadowProps, useShadowCss } from '../../fundamentals/shadows/shadows';
import { IBorderRadiusProps, usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { IMediaSizeProps, MediaSize, useMediaSizeCss } from '../../fundamentals/sizes/mediaSize';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { EORA_SPACES, IGapSizeProps, IGutterProps, useColumnGapCss, useGutterCss } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Heading4, Heading5 } from '../../components/Headings/Headings';
import { ICtaButtonBarProps } from '../../components/CtaButtonBar/CtaButtonBar';
import { GapContainer, SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';
import { HR } from '../../components/Divider/Divider';
import { IBulletProps } from './Bullet';
import { EoraAssetsContext } from '../../themes/EoraThemeProvider';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { IAlertProps } from '../../components/Alert/Alert';

export interface ITileProps extends IShadowProps, IGutterProps, IIconBaseProps {
  image?: string;
  imageSize?: MediaSize;
  title?: string;
  titleBadge?: string;
  subtitle?: string;
  ctaButtonBar?: ReactElement<ICtaButtonBarProps>;
  bullets?: ReactElement<IBulletProps> | ReactElement<IBulletProps>[];
  hideHr?: boolean;
  alert?: ReactElement<IAlertProps>;
}

interface IResponsiveProps {
  isMobile: boolean;
}

const TileContainer = styled(BaseDiv)<IShadowProps & IBorderRadiusProps & IGutterProps>`
  ${usePanelBorderRadiusCss()};
  ${useShadowCss()}
  ${useGutterCss()};
  min-width: ${PX_TO_REM[336]};
`;

const TileBody = styled(BaseDiv)<IGutterProps & IGapSizeProps & IResponsiveProps>`
  ${useGutterCss()};
  ${useColumnGapCss()};
  margin: ${({ theme, isMobile }) => theme.eoraSpaces[isMobile ? EORA_SPACES.LARGE : EORA_SPACES.XLARGE]};
`;

const TileImage = styled(BaseImg)<IMediaSizeProps & IBorderRadiusProps>`
  display: block;
  ${usePanelBorderRadiusCss(true)};
  width: 100%;
  ${useMediaSizeCss}
`;

const TitleContainer = styled(GapContainer)`
  justify-content: space-between;
`;

const ButtonsContainer = styled(SpaceContainer)`
  padding-top: ${({ theme }) => theme.eoraSpaces[EORA_SPACES.MEDIUM]};
`;

const BulletContainer = styled(GapContainer)<IResponsiveProps>`
  ${useGutterCss()};
  flex-direction: column;
  gap: ${({ isMobile, theme }) => theme.eoraSpaces[isMobile ? EORA_SPACES.LARGE : EORA_SPACES.XLARGE]};
`;

const StyledHeading5 = styled(Heading5)`
  line-height: 32px;
`;

export const Tile: React.FC<ITileProps> = ({ image, imageSize, title, titleBadge, subtitle, ctaButtonBar, bullets, hideHr, alert, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Tile');
  const { baseAssetsUrl } = useContext(EoraAssetsContext);
  const imgSrc = `${baseAssetsUrl}${image}`;
  const { isMobile } = useWindowDimensions();

  return (
    <TileContainer shadow {...generateTestId()} {...rest}>
      {image && <TileImage src={imgSrc} mediaSize={imageSize ?? 'uwide'} />}
      <TileBody isMobile={isMobile} {...generateTestId('Body')}>
        <TitleContainer>
          {title && (
            <StyledHeading5 headingType={'SEMI_BOLD'} gutter={false}>
              {title}
            </StyledHeading5>
          )}
          {titleBadge && <Badge light size='small' variant='info' label={titleBadge} />}
        </TitleContainer>
        {subtitle && (
          <Heading4 headingType='SEMI_BOLD' gutter={false}>
            {subtitle}
          </Heading4>
        )}
        {ctaButtonBar && <ButtonsContainer>{ctaButtonBar}</ButtonsContainer>}
        {!hideHr && <HR />}
        {alert && <>{alert}</>}
        {bullets && <BulletContainer isMobile={isMobile}>{bullets}</BulletContainer>}
      </TileBody>
    </TileContainer>
  );
};
